import { withTranslation, TFunction } from "react-i18next";
import { useLocation } from "react-router-dom";
import { SvgIcon } from "../../common/SvgIcon";
import Container from "../../common/Container";
import LanguageSelector from "../LanguageSelector";
import { useContent } from "../../content";

import {
  FooterSection,
  HorizontalLine,
  FooterTopRow,
  FooterLogoTagline,
  LargeText,
  BoldText,
  FooterMenusWrapper,
  FooterMenuColumn,
  TextBold,
  FooterLinks,
  FooterLink,
  FooterContact,
  FooterBottomRow,
  FooterCopyrightWrapper,
  SmallText,
  CopyrightDivider,
  FooterCTA,
  FooterWrapper,
  LaunchButton
} from "./styles";

const Footer = ({ t, i18n }: { t: TFunction; i18n: any }) => {
  // Get footer content in the current language
  const footerContent = useContent('footer');
  const location = useLocation();

  // Function to generate language-aware URLs
  const getLangUrl = (path: string): string => {
    const currentLanguage = i18n.language;
    
    // If we're already in a language context from URL
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const firstSegment = pathSegments[0];
    
    // Check if the current path already has a language prefix
    const isLanguagePrefix = ['en', 'es', 'fr', 'de', 'it', 'da', 'sv', 'no', 'nl'].includes(firstSegment);
    
    // Use current language from URL if available, otherwise use i18n language
    const languageToUse = isLanguagePrefix ? firstSegment : currentLanguage;
    
    // For English (default) or if we're already at the default path
    if (languageToUse === 'en') {
      return path;
    }
    
    // For other languages, add the prefix
    return `/${languageToUse}${path}`;
  };

  return (
    <FooterSection>
      <Container>
        <FooterWrapper>
          <HorizontalLine />
          
          <FooterTopRow>
            <FooterLogoTagline>
              <a href={getLangUrl("/")}>
                <SvgIcon 
                  src="logo.png" 
                  aria-label="homepage" 
                  height="30px" 
                  width="Auto" 
                />
              </a>
              <LargeText>
                <BoldText dangerouslySetInnerHTML={{ __html: footerContent.tagline }} />
              </LargeText>
            </FooterLogoTagline>
            
            <FooterCTA>
              <LaunchButton href="http://app.totext.eu">
                {footerContent.launchApp}
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="icon-embed-xxsmall">
                  <path 
                    fillRule="evenodd" 
                    clipRule="evenodd" 
                    d="M12.9697 3.96967C13.2626 3.67678 13.7374 3.67678 14.0303 3.96967L21.5303 11.4697C21.671 11.6103 21.75 11.8011 21.75 12C21.75 12.1989 21.671 12.3897 21.5303 12.5303L14.0303 20.0303C13.7374 20.3232 13.2626 20.3232 12.9697 20.0303C12.6768 19.7374 12.6768 19.2626 12.9697 18.9697L19.1893 12.75H3C2.58579 12.75 2.25 12.4142 2.25 12C2.25 11.5858 2.58579 11.25 3 11.25H19.1893L12.9697 5.03033C12.6768 4.73744 12.6768 4.26256 12.9697 3.96967Z" 
                    fill="currentColor"
                  ></path>
                </svg>
              </LaunchButton>
            </FooterCTA>
          </FooterTopRow>

          <FooterMenusWrapper>
            <FooterMenuColumn>
              <TextBold>{footerContent.navigation}</TextBold>
              <FooterLinks>
                <FooterLink href={getLangUrl("/")}>{footerContent.home}</FooterLink>
                <FooterLink href={getLangUrl("/pricing")}>{footerContent.pricing}</FooterLink>
                <FooterLink href={getLangUrl("/security")}>{footerContent.security}</FooterLink>
              </FooterLinks>
            </FooterMenuColumn>

            <FooterMenuColumn>
              <TextBold>{footerContent.contact}</TextBold>
              <FooterContact>
                <FooterLinks>
                  <FooterLink href="mailto:anders@totext.eu">Anders@totext.eu</FooterLink>
                </FooterLinks>
              </FooterContact>
            </FooterMenuColumn>
          </FooterMenusWrapper>

          <FooterBottomRow>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              width: '100%',
              flexWrap: 'wrap',
              gap: '1rem'
            }}>
              <FooterCopyrightWrapper>
                <SmallText>{footerContent.allRightsReserved}</SmallText>
              </FooterCopyrightWrapper>
              
              {/* Language Selector in footer */}
              <div style={{ 
                display: 'flex', 
                alignItems: 'center',
                marginLeft: 'auto' 
              }}>
                <SmallText style={{ marginRight: '8px' }}>{footerContent.language}:</SmallText>
                <LanguageSelector />
              </div>
            </div>
          </FooterBottomRow>
        </FooterWrapper>
      </Container>
    </FooterSection>
  );
};

export default withTranslation()(Footer);